// @flow strict
import { BLOCKS } from '@contentful/rich-text-types'
import {
  CMSSections,
  getLocalisedFieldsFromNode,
  getNodeType,
  type SectionRendererMap,
} from '@mondough/contentful'
import * as React from 'react'
import { locale } from '../Global'
import { bespokeSectionMap } from './bespokeRenderers/registerBespokeSection'
import {
  paidHeroSection,
  paidTextAndImageSection,
  paidTriptych,
} from './paidThemeComponents'

import {
  businessCTASection,
  businessHeroSection,
  businessTriptych,
  businessTextAndImage,
  businessMarkdownSection,
  businessBlogRollSection,
} from './businessThemeComponents'
import {
  defaultHero,
  defaultTextAndImage,
  defaultTriptych,
  defaultEmailSignUpForm,
  defaultMarkdownSection,
  defaultCTASection,
  defaultBlogRoll,
  defaultBanner,
  defaultDiscloureGroup,
} from './defaultThemeComponents'
import { usaHero, usaTextAndImage } from './usaThemeComponents'

export type PageTheme = 'default' | 'paid' | 'business' | 'usa'

// We need explicit undefined return for certain Nodes
const noop = (nodeOrText: mixed) => undefined

const defaultTheme = {
  cmsSectionHero: defaultHero,
  cmsSectionTextImage: defaultTextAndImage,
  cmsSectionCallToAction: defaultCTASection,
  cmsSectionTriptych: defaultTriptych,
  // Note: The content model in Contentful only allows users to add
  // tables to the entries created from the Markdown content type but
  // in theory it could take any markdown. One to keep in mind if we ever need
  // other types of markdown rendering.
  cmsSectionMarkdown: defaultMarkdownSection,
  // And a bespoke section for hard-coded, single use components. See registerBesopkeSection.js for instructions
  cmsSectionEmailSignupForm: defaultEmailSignUpForm,
  cmsSectionBlogRoll: defaultBlogRoll,
  cmsBanner: defaultBanner,
  cmsDisclosureGroup: defaultDiscloureGroup,
}

const paidTheme = {
  cmsSectionHero: paidHeroSection,
  cmsSectionTextImage: paidTextAndImageSection,
  cmsSectionCallToAction: defaultCTASection,
  cmsSectionTriptych: paidTriptych,
  cmsSectionMarkdown: defaultMarkdownSection,
  cmsSectionEmailSignupForm: defaultEmailSignUpForm,
  cmsSectionBlogRoll: defaultBlogRoll,
  cmsBanner: defaultBanner,
  cmsDisclosureGroup: defaultDiscloureGroup,
}

const businessTheme: SectionRendererMap<React.Node> = {
  cmsSectionHero: businessHeroSection,
  cmsSectionTextImage: businessTextAndImage,
  cmsSectionCallToAction: businessCTASection,
  cmsSectionTriptych: businessTriptych,
  cmsSectionMarkdown: businessMarkdownSection,
  cmsSectionEmailSignupForm: defaultEmailSignUpForm,
  cmsSectionBlogRoll: businessBlogRollSection,
  cmsBanner: defaultBanner,
  cmsDisclosureGroup: defaultDiscloureGroup,
}
const usaTheme: SectionRendererMap<React.Node> = {
  cmsSectionHero: usaHero,
  cmsSectionTextImage: usaTextAndImage,
  cmsSectionCallToAction: defaultCTASection,
  cmsSectionTriptych: defaultTriptych,
  cmsSectionMarkdown: defaultMarkdownSection,
  cmsSectionEmailSignupForm: defaultEmailSignUpForm,
  cmsSectionBlogRoll: defaultBlogRoll,
  cmsBanner: defaultBanner,
  cmsDisclosureGroup: defaultDiscloureGroup,
}

/** Heads up! When adding a new theme here, you'll also
 * need to appease the flow gods over on the FeaturePage
 * component by adding the new theme to a boolean check (called pageTheme)*/
export const themesRegister: {
  [k: PageTheme]: SectionRendererMap<React.Node>,
} = {
  default: defaultTheme,
  paid: paidTheme,
  business: businessTheme,
  usa: usaTheme,
}

export const renderFeaturePageOptions = (theme: PageTheme) => {
  const sectionToComponentMap = {
    ...themesRegister[theme || 'default'],
    [CMSSections.BESPOKE]: node => {
      const { bespokeComponentName } =
        getLocalisedFieldsFromNode(node, locale) || {}
      const render = bespokeSectionMap[bespokeComponentName]
      return (render && render(node)) || null
    },
  }

  return {
    // This is an explicit no-op
    renderText: noop,
    renderNode: {
      [BLOCKS.EMBEDDED_ENTRY]: node => {
        const type = getNodeType(node)
        return (
          type &&
          sectionToComponentMap[type] &&
          sectionToComponentMap[type](node)
        )
      },
      // This is an explicit no-op
      [BLOCKS.PARAGRAPH]: noop,
    },
  }
}
